import React, { useState, useEffect, useRef } from 'react';

const BulkVideoGenerator = ({ videoJobs, user, onAllComplete, onProgressUpdate }) => {
  const [jobs, setJobs] = useState([]);
  const [allCompleted, setAllCompleted] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const completedJobsRef = useRef(new Set());

  useEffect(() => {
    if (videoJobs && videoJobs.length > 0) {
      const initialJobs = videoJobs.map((jobData, index) => ({
        id: index,
        jobData: jobData,
        status: 'pending',
        progress: 0,
        jobId: null,
        videoUrl: null,
        error: null
      }));
      setJobs(initialJobs);
      
      // Start all video generation jobs
      initialJobs.forEach(job => {
        startVideoGeneration(job);
      });
    }
  }, [videoJobs]);

  useEffect(() => {
    // Calculate overall progress
    if (jobs.length > 0) {
      const totalProgress = jobs.reduce((sum, job) => sum + job.progress, 0);
      const avgProgress = totalProgress / jobs.length;
      setOverallProgress(avgProgress);
      
      if (onProgressUpdate) {
        onProgressUpdate(avgProgress);
      }
    }
  }, [jobs, onProgressUpdate]);

  useEffect(() => {
    // Check if all jobs are completed
    const completedJobs = jobs.filter(job => job.status === 'completed').length;
    if (jobs.length > 0 && completedJobs === jobs.length && !allCompleted) {
      setAllCompleted(true);
      if (onAllComplete) {
        onAllComplete(jobs);
      }
    }
  }, [jobs, allCompleted, onAllComplete]);

  const startVideoGeneration = async (job) => {
    try {
      // Update job status to processing
      updateJobStatus(job.id, { status: 'processing', progress: 0 });

      // Process audio selection for server
      let audioSelection = null;
      if (job.jobData.tweetData?.audioSelection) {
        const { type, file, libraryFilename, libraryUrl } = job.jobData.tweetData.audioSelection;
        audioSelection = { type, libraryFilename, libraryUrl };
        
        // Convert custom audio file to base64 if needed
        if (type === 'custom' && file) {
          const audioBase64 = await convertFileToBase64(file);
          audioSelection.audioData = audioBase64; // Server expects 'audioData', not 'file'
        }
      }

      // Submit video generation job to remote server
      const response = await fetch('/api/video/render-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tweetImage: job.jobData.imageData,
          tweetData: job.jobData.tweetData,
          audioSelection,
          circleOverlays: job.jobData.tweetData?.circleOverlays || [], // Add circle overlays
          userId: user?.uid
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit video job');
      }

      const jobResult = await response.json();
      updateJobStatus(job.id, { jobId: jobResult.jobId, status: 'queued' });
      
      // Poll for job completion
      await pollJobStatus(job.id, jobResult.jobId);
      
    } catch (error) {
      console.error(`Error generating video for job ${job.id}:`, error);
      updateJobStatus(job.id, { 
        status: 'failed', 
        error: error.message,
        progress: 0 
      });
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const pollJobStatus = async (jobIndex, jobId) => {
    let attempts = 0;
    const maxAttempts = 750; // 10 minutes max (0.8 second intervals)
    
    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`/api/video/job-status/${jobId}`);
        
        if (!response.ok) {
          throw new Error('Failed to check job status');
        }
        
        const status = await response.json();
        
        // Update job progress
        updateJobStatus(jobIndex, { 
          progress: status.progress || 0,
          status: status.status === 'active' ? 'processing' : 
                 status.status === 'waiting' ? 'queued' : status.status
        });
        
        if (status.status === 'completed') {
          await downloadVideo(jobIndex, jobId, status.firebase);
          break;
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Video generation failed');
        }
        
        // Wait 0.8 seconds before next check
        await new Promise(resolve => setTimeout(resolve, 800));
        attempts++;
        
      } catch (error) {
        console.error(`Error checking job status for job ${jobIndex}:`, error);
        updateJobStatus(jobIndex, { 
          status: 'failed', 
          error: error.message,
          progress: 0 
        });
        return;
      }
    }
    
    if (attempts >= maxAttempts) {
      updateJobStatus(jobIndex, { 
        status: 'failed', 
        error: 'Video generation timed out',
        progress: 0 
      });
    }
  };

  const downloadVideo = async (jobIndex, jobId, firebaseUrls = null) => {
    try {
      const response = await fetch(`/api/video/download/${jobId}`);
      if (!response.ok) {
        throw new Error('Failed to download video');
      }
      const videoBlob = await response.blob();
      
      // Trigger local download
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `twizzler-bulk-${jobIndex + 1}-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      updateJobStatus(jobIndex, { 
        status: 'completed', 
        progress: 100,
        videoUrl: firebaseUrls?.videoUrl || url
      });
      
    } catch (error) {
      console.error(`Error downloading video for job ${jobIndex}:`, error);
      updateJobStatus(jobIndex, { 
        status: 'failed', 
        error: error.message,
        progress: 0 
      });
    }
  };

  const updateJobStatus = (jobIndex, updates) => {
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === jobIndex ? { ...job, ...updates } : job
      )
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'queued': return '🕐';
      case 'processing': return '⚡';
      case 'completed': return '✅';
      case 'failed': return '❌';
      default: return '⏳';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'queued': return 'Queued';
      case 'processing': return 'Processing';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  if (!videoJobs || videoJobs.length === 0) {
    return null;
  }

  return (
    <div className="bulk-video-generator">
      <div className="bulk-progress-header">
        <h3>Bulk Video Generation</h3>
        <div className="overall-progress">
          <div className="progress-bar-wrapper">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
            <span className="progress-text">{Math.round(overallProgress)}%</span>
          </div>
          <p className="progress-status">
            {jobs.filter(job => job.status === 'completed').length} of {jobs.length} videos completed
          </p>
        </div>
      </div>

      <div className="job-list">
        {jobs.map(job => (
          <div key={job.id} className="job-item">
            <div className="job-info">
              <span className="job-icon">{getStatusIcon(job.status)}</span>
              <span className="job-title">
                Video {job.id + 1}: @{job.jobData.tweetData.username}
              </span>
              <span className="job-status">{getStatusText(job.status)}</span>
            </div>
            
            {job.status === 'processing' || job.status === 'queued' ? (
              <div className="job-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${job.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(job.progress)}%</span>
              </div>
            ) : job.status === 'failed' ? (
              <div className="job-error">
                <span className="error-text">{job.error}</span>
              </div>
            ) : job.status === 'completed' ? (
              <div className="job-complete">
                <span className="complete-text">Downloaded successfully!</span>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {allCompleted && (
        <div className="bulk-complete">
          <p className="completion-message">
            🎉 All {jobs.length} videos have been generated and downloaded!
          </p>
        </div>
      )}
    </div>
  );
};

export default BulkVideoGenerator;
